/**
 * useMergedPermissions – Hook đọc quyền từ DB (Enterprise version)
 * - Sử dụng TanStack Query để quản lý RAM cache cực nhanh
 * - Tự động cập nhật UI Realtime khi Admin sửa quyền trên Database (Supabase Subscription)
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { useUserRoles } from '@/core/roles/useUserRoles';
import { permissionService } from '../services/permission.service';
import type { Module, Action } from '@/config/permission-matrix.config';
import type { UserRole } from '@/config/navigation.config';
import { supabase } from '@/core/database/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface MergedPermissionsReturn {
  can: (module: Module, action: Action) => boolean;
  canAll: (module: Module, actions: Action[]) => boolean;
  canAny: (module: Module, actions: Action[]) => boolean;
  permissions: Partial<Record<Module, Action[]>>;
  roleCode: UserRole;
  allRoleCodes: UserRole[];
  isLoading: boolean;
  refresh: () => void;
}

export function useMergedPermissions(): MergedPermissionsReturn {
  const { user } = useCurrentUser();
  const primaryRole = (user?.roleCode ?? 'staff') as UserRole;
  const { allRoleCodes } = useUserRoles(user?.id ?? null, primaryRole);
  const queryClient = useQueryClient();

  const queryKey = ['user_permissions', user?.id, [...allRoleCodes].sort().join(',')];

  const { data: permissions = {}, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user?.id) return {};
      const res = await permissionService.getMergedPermissions(user.id, primaryRole, allRoleCodes);
      return res.data ?? {};
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache trên RAM 5 phút
    gcTime: 10 * 60 * 1000,
  });

  // Realtime Subscription: Lắng nghe thay đổi từ Database
  useEffect(() => {
    if (!user?.id) return;

    let channel: RealtimeChannel | null = null;

    const timer = window.setTimeout(() => {
      channel = supabase
        .channel(`permissions_changes:${user.id}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'user_permissions', filter: `user_id=eq.${user.id}` },
          (payload) => {
            console.info('[Realtime] Quyền ngoại lệ của bạn vừa được Admin cập nhật:', payload);
            queryClient.invalidateQueries({ queryKey: ['user_permissions', user.id] });
          }
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'role_permissions' },
          (payload) => {
            console.info('[Realtime] Quyền Vai trò hệ thống vừa được Admin cập nhật:', payload);
            queryClient.invalidateQueries({ queryKey: ['user_permissions', user.id] });
          }
        )
        .subscribe();
    }, 250);

    return () => {
      window.clearTimeout(timer);
      if (channel) {
        void supabase.removeChannel(channel);
      }
    };
  }, [user?.id, queryClient]);

  // TỐI ƯU O(1): Biến mảng Array thành Hash Map để tra cứu siêu tốc
  const permissionsMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    if (!permissions) return map;
    
    Object.entries(permissions).forEach(([module, actions]) => {
      (actions as Action[]).forEach(action => {
        map[`${module}:${action}`] = true;
      });
    });
    return map;
  }, [permissions]);

  const can = useCallback(
    (module: Module, action: Action): boolean => {
      if (allRoleCodes.includes('super_admin')) return true;
      return !!permissionsMap[`${module}:${action}`];
    },
    [permissionsMap, allRoleCodes]
  );

  const canAll = useCallback(
    (module: Module, actions: Action[]): boolean => {
      if (allRoleCodes.includes('super_admin')) return true;
      return actions.every((a) => !!permissionsMap[`${module}:${a}`]);
    },
    [permissionsMap, allRoleCodes]
  );

  const canAny = useCallback(
    (module: Module, actions: Action[]): boolean => {
      if (allRoleCodes.includes('super_admin')) return true;
      return actions.some((a) => !!permissionsMap[`${module}:${a}`]);
    },
    [permissionsMap, allRoleCodes]
  );

  return {
    can,
    canAll,
    canAny,
    permissions,
    roleCode: primaryRole,
    allRoleCodes,
    isLoading,
    refresh: refetch,
  };
}
